// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ListItem } from "@insite/mobius/Lists";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import Modal, { ModalPresentationProps } from "@store/MobiusOverrides/Modal/Modal";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { GetInvoicesApiParameter } from "@store/Extensions/Store/Services/InvoiceService";
import { getDocumentsWrapper } from "@store/Framework/Common/Utilities/getDocumentsWrapper";
import useTrackExportHistory from "@store/Framework/GoogleAnalytics/hooks/useTrackExportHistory";
import { getInvoiceHistory } from "@store/Framework/Services/documentService";
import { useGreifInvoiceExportSettings } from "@store/Framework/Store/Context/ContextHooks";
import { historyFilterProps } from "@store/Themes/common/props/history";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    parameter: state.greif.pages.invoiceHistory.getInvoicesParameter,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface InvoiceHistorySearchExportButtonStyles {
    exportButton?: ButtonPresentationProps;
    wrapper?: InjectableCss;
    exportInvoiceHistoryModal?: ModalPresentationProps;
    flexWrapper?: FlexWrapperPresentationProps;
    conditionWarning?: TypographyPresentationProps;
}

export const exportButtonStyles: InvoiceHistorySearchExportButtonStyles = {
    exportButton: historyFilterProps.exportButton,
    wrapper: {
        css: css`
            display: flex;
            justify-content: right;
            width: 100%;
        `,
    },
    exportInvoiceHistoryModal: {
        sizeVariant: "large",
        headlineTypographyProps: { forwardAs: "h2" },
    },
    flexWrapper: {
        extendedStyles: {
            content: {
                css: css`
                    flex-direction: column;
                    gap: ${pxToRem(10)};
                `,
            },
        },
    },
    conditionWarning: {
        weight: "bold",
    },
};

const styles = exportButtonStyles;

const InvoiceHistorySearchExportButton: React.FC<Props> = ({ parameter }) => {
    const [isExportInvoiceHistoryModalOpen, setisExportInvoiceHistoryModalOpen] = React.useState(false);
    const [invoiceHistoryIsLoading, setInvoiceHistoryIsLoading] = React.useState(false);

    const [fromInvoiceDateCompare, setFromInvoiceDateCompare] = React.useState<Date>();
    const [toInvoiceDateCompare, setToInvoiceDateCompare] = React.useState<Date>();
    const [fromDueDateCompare, setFromDueDateCompare] = React.useState<Date>();
    const [toDueDateCompare, setToDueDateCompare] = React.useState<Date>();

    const toasterContext = React.useContext(ToasterContext);
    const invoicesDataView = usePersistentDataView(React.useContext(InvoicesDataViewContext));
    const trackExportHistory = useTrackExportHistory();
    const today = new Date();

    const greifSettings = useGreifInvoiceExportSettings();
    const isEnforcedMaxInvoices = greifSettings?.enforceMaxNumberOfImports;
    const isEnforcedMaxDays = greifSettings?.enforceMaxDateRangeOfImports;
    const maxNumberOfInvoices = greifSettings?.maxNumberOfImports || 50;
    const maxDateRangeDays = greifSettings?.maxDateRangeOfImports || 90;

    const areBothSettingsEnforced =
        greifSettings?.enforceMaxNumberOfImports && greifSettings?.enforceMaxDateRangeOfImports;

    let totalItemCount = 0;

    const openExportInvoiceHistoryModal = () => {
        setisExportInvoiceHistoryModalOpen(true);
    };

    const closeExportInvoiceHistoryModal = () => {
        setisExportInvoiceHistoryModalOpen(false);
    };

    const dateDiffInDays = (a: Date, b: Date) => {
        const _MS_PER_DAY = 1000 * 60 * 60 * 24;
        // Discard the time and time-zone information.
        const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
        const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

        return Math.floor((utc2 - utc1) / _MS_PER_DAY);
    };

    React.useEffect(() => {
        const fromInvoiceDate = new Date(parameter.fromDate!);
        const toInvoiceDate = new Date(parameter.toDate || today);
        const fromDueDate = new Date(parameter.dueDateTo!);
        const toDueDate = new Date(parameter.dueDateFrom || today);
        setFromInvoiceDateCompare(fromInvoiceDate);
        setToInvoiceDateCompare(toInvoiceDate);
        setFromDueDateCompare(fromDueDate);
        setToDueDateCompare(toDueDate);
    }, [parameter]);

    if (invoicesDataView.value && invoicesDataView.pagination) {
        totalItemCount = invoicesDataView?.pagination.totalItemCount;
    }

    const checkEnforcementAndValueValidity = (value: number, allowedMaxValue: number, greater?: boolean) => {
        if (!!value || value > 0) {
            return greater ? value >= allowedMaxValue : value <= allowedMaxValue;
        }
        return false;
    };

    const exportInvoiceHistory = () => {
        const noEnforcing = !greifSettings?.enforceMaxNumberOfImports && !greifSettings?.enforceMaxDateRangeOfImports;
        const numberOfInvoiceDaysFiltered = dateDiffInDays(fromInvoiceDateCompare!, toInvoiceDateCompare!);
        const numberOfDeliveryDaysFiltered = dateDiffInDays(fromDueDateCompare!, toDueDateCompare!);

        const isValidNumberOfInvoices = checkEnforcementAndValueValidity(totalItemCount, maxNumberOfInvoices);
        const isValidInvoiceDateRangeDays = checkEnforcementAndValueValidity(
            numberOfInvoiceDaysFiltered,
            maxDateRangeDays,
        );
        const isValidDelieveryDateRangeDays = checkEnforcementAndValueValidity(
            numberOfDeliveryDaysFiltered,
            maxDateRangeDays,
        );

        const isValidDateRanges = () => {
            if (numberOfInvoiceDaysFiltered && !numberOfDeliveryDaysFiltered) {
                return isValidInvoiceDateRangeDays;
            }
            if (numberOfDeliveryDaysFiltered && !numberOfInvoiceDaysFiltered) {
                return isValidDelieveryDateRangeDays;
            }
            if (numberOfInvoiceDaysFiltered && numberOfDeliveryDaysFiltered) {
                const greaterDateFrom =
                    fromInvoiceDateCompare! < fromDueDateCompare! ? fromInvoiceDateCompare : fromDueDateCompare;

                const greaterDateTo =
                    toInvoiceDateCompare! < toDueDateCompare! ? toInvoiceDateCompare : toDueDateCompare;

                const numberOfCombinedDaysFilters = dateDiffInDays(greaterDateFrom!, greaterDateTo!);

                return checkEnforcementAndValueValidity(numberOfCombinedDaysFilters, maxDateRangeDays);
            }
            return false;
        };

        const canExport = () => {
            if (isEnforcedMaxInvoices && isEnforcedMaxDays) {
                return isValidNumberOfInvoices && isValidDateRanges();
            }
            if (isEnforcedMaxInvoices && !isEnforcedMaxDays) {
                return isValidNumberOfInvoices;
            }
            if (!isEnforcedMaxInvoices && isEnforcedMaxDays) {
                return isValidDateRanges();
            }
            return false;
        };

        if (noEnforcing || canExport()) {
            getDocumentsWrapper(
                getInvoiceHistory,
                setInvoiceHistoryIsLoading,
                { parameter: window.location.search },
                toasterContext,
            ).then(e => {
                if (e) {
                    trackExportHistory({ file_category: "InvoiceHistory export" });
                }
            });
        } else {
            openExportInvoiceHistoryModal();
        }
    };

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <Button
                    {...styles.exportButton}
                    data-test-selector="invoiceHistory_exportFilters"
                    onClick={exportInvoiceHistory}
                    isLoading={invoiceHistoryIsLoading}
                    disabled={invoicesDataView.isLoading || totalItemCount === 0}
                >
                    {translate("Invoice History")}
                </Button>
            </StyledWrapper>
            <Modal
                size={500}
                headline={translate("Invoice History Export Document")}
                isOpen={isExportInvoiceHistoryModalOpen}
                handleClose={closeExportInvoiceHistoryModal}
            >
                <FlexWrapper {...styles.flexWrapper}>
                    <Typography {...styles.conditionWarning}>
                        {areBothSettingsEnforced
                            ? translate("Please ensure that the conditions below are respected.")
                            : translate("Please ensure that the condition below is respected.")}
                    </Typography>

                    {isEnforcedMaxInvoices && (
                        <ListItem>
                            <Typography>
                                {siteMessage("InvoiceHistory_CantExport_NumberInvoices", String(maxNumberOfInvoices))}
                            </Typography>
                        </ListItem>
                    )}
                    {isEnforcedMaxDays && (
                        <ListItem>
                            <Typography>
                                {siteMessage("InvoiceHistory_CantExport_DateRangeDays", String(maxDateRangeDays))}
                            </Typography>
                        </ListItem>
                    )}
                </FlexWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(InvoiceHistorySearchExportButton),
    definition: {
        group: "Invoice History",
        displayName: "Export History Button",
        allowedContexts: ["InvoiceHistoryPage"],
    },
};

export default widgetModule;
